import { useRef, useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import { useSetPassword } from "ra-supabase-core";
import { useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Layout } from "@/components/supabase/layout";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface SetPasswordFormData {
  password: string;
  confirmPassword: string;
}

interface SessionTokens {
  access_token: string;
  refresh_token: string;
}

const EMAIL_OTP_TYPES = ["invite", "recovery"] as const;
type EmailOtpType = (typeof EMAIL_OTP_TYPES)[number];

const isEmailOtpType = (value: string | null): value is EmailOtpType =>
  EMAIL_OTP_TYPES.includes(value as EmailOtpType);

// Email links carry a one-time token_hash instead of a session, and it is only
// exchanged on submit: mail scanners (e.g. Outlook Safe Links) open links before
// the user does, which would otherwise consume the token.
const exchangeTokenHash = async (
  token_hash: string,
  type: EmailOtpType,
): Promise<SessionTokens> => {
  const { data, error } = await getSupabaseClient().auth.verifyOtp({
    token_hash,
    type,
  });
  if (error) throw error;
  if (!data.session) throw new Error("ra-supabase.auth.missing_tokens");
  return {
    access_token: data.session.access_token,
    refresh_token: data.session.refresh_token,
  };
};

export const SetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const access_token = searchParams.get("access_token");
  const refresh_token = searchParams.get("refresh_token");
  const token_hash = searchParams.get("token_hash");
  const type = searchParams.get("type");
  const hasTokenHash = token_hash != null && isEmailOtpType(type);

  // The token_hash is single-use: keep the session it yields so a failed
  // password update (e.g. too weak) can be retried without a new email.
  const sessionRef = useRef<SessionTokens | null>(
    access_token && refresh_token ? { access_token, refresh_token } : null,
  );

  const notify = useNotify();
  const translate = useTranslate();
  const [, { mutateAsync: setPassword }] = useSetPassword();

  const validate = (values: SetPasswordFormData) => {
    if (values.password !== values.confirmPassword) {
      return {
        password: "ra-supabase.validation.password_mismatch",
        confirmPassword: "ra-supabase.validation.password_mismatch",
      };
    }
    return {};
  };

  if (!sessionRef.current && !hasTokenHash) {
    if (process.env.NODE_ENV === "development") {
      console.error("Missing access_token or refresh_token for set password");
    }
    return (
      <Layout>
        <p>{translate("ra-supabase.auth.missing_tokens")}</p>
      </Layout>
    );
  }

  const submit = async (values: SetPasswordFormData) => {
    try {
      setLoading(true);
      if (!sessionRef.current && hasTokenHash) {
        sessionRef.current = await exchangeTokenHash(token_hash, type);
      }
      if (!sessionRef.current) {
        throw new Error("ra-supabase.auth.missing_tokens");
      }
      await setPassword({
        ...sessionRef.current,
        password: values.password,
      });
    } catch (error: any) {
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
            ? "ra.auth.sign_in_error"
            : error.message,
        {
          type: "warning",
          messageArgs: {
            _:
              typeof error === "string"
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.set_password.new_password", {
            _: "Choose your password",
          })}
        </h1>
      </div>
      <Form
        className="space-y-8"
        onSubmit={submit as any}
        validate={validate as any}
      >
        <TextInput
          label={translate("ra.auth.password", {
            _: "Password",
          })}
          autoComplete="new-password"
          source="password"
          type="password"
          validate={required()}
        />
        <TextInput
          label={translate("crm.auth.confirm_password", {
            _: "Confirm password",
          })}
          source="confirmPassword"
          type="password"
          validate={required()}
        />
        <Button type="submit" className="cursor-pointer" disabled={loading}>
          {translate("ra.action.save")}
        </Button>
      </Form>
    </Layout>
  );
};

SetPasswordPage.path = "set-password";
